import { Capacitor, CapacitorHttp } from '@capacitor/core';

const API_BASE = '/api/my-dohm';
const PUBLIC_APP_ORIGIN = 'https://app.frozensave.com';
const REQUEST_MARKER = 'handoff-v1';
const BROWSER_CONTRACT_VERSION = 'dohm.identity-federation.v1.6';
const IDENTITY_FEDERATION_CONTRACT_VERSION = 'dohm.identity-federation.v1.10';

type FederationState =
  | 'connected'
  | 'contact_verification_available'
  | 'contact_verification_queued'
  | 'contact_verified'
  | 'missing_contact'
  | 'pending'
  | 'pending_contact_verification'
  | 'unavailable';

export interface MyDohmFederationStatus {
  readonly state: FederationState;
  readonly nativeEntryReady?: boolean;
}

const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value != null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;

const request = async (
  path: string,
  init: { readonly body?: unknown; readonly method?: 'DELETE' | 'GET' | 'POST' } = {},
): Promise<Record<string, unknown>> => {
  if (Capacitor.isNativePlatform()) {
    const response = await CapacitorHttp.request({
      url: `${PUBLIC_APP_ORIGIN}${API_BASE}${path}`,
      method: init.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        Origin: PUBLIC_APP_ORIGIN,
        'X-Frozen-Save-Request': REQUEST_MARKER,
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(init.body === undefined ? {} : { data: init.body }),
      connectTimeout: 15_000,
      readTimeout: 15_000,
      disableRedirects: true,
    });
    const cache = Object.entries(response.headers).find(([name]) => name.toLowerCase() === 'cache-control')?.[1];
    if (!cache?.split(',').some(value => value.trim() === 'no-store')) throw new Error('MY_DOHM_CACHE_POLICY_INVALID');
    let value: Record<string, unknown> | null = null;
    try { value = record(typeof response.data === 'string' ? JSON.parse(response.data) : response.data); } catch { /* invalid response */ }
    if (response.status < 200 || response.status >= 300) throw new Error(typeof value?.code === 'string' ? value.code : 'MY_DOHM_UNAVAILABLE');
    if (value?.contractVersion !== BROWSER_CONTRACT_VERSION ||
        value.identityFederationContractVersion !== IDENTITY_FEDERATION_CONTRACT_VERSION) throw new Error('MY_DOHM_RESPONSE_INVALID');
    return value;
  }
  const response = await fetch(`${API_BASE}${path}`, {
    ...(init.body == null ? {} : { body: JSON.stringify(init.body) }),
    credentials: 'same-origin',
    headers: {
      ...(init.body == null ? {} : { 'Content-Type': 'application/json' }),
      'X-Frozen-Save-Request': REQUEST_MARKER,
    },
    method: init.method ?? 'GET',
  });
  const value = record(await response.json().catch(() => null));
  if (!response.ok) throw new Error(typeof value?.code === 'string' ? value.code : 'MY_DOHM_UNAVAILABLE');
  if (
    value?.contractVersion !== BROWSER_CONTRACT_VERSION ||
    value.identityFederationContractVersion !== IDENTITY_FEDERATION_CONTRACT_VERSION
  ) throw new Error('MY_DOHM_RESPONSE_INVALID');
  return value;
};

export const myDohmFederation = {
  async status(): Promise<MyDohmFederationStatus> {
    const value = await request('/status');
    const state = value.contactState ?? value.state;
    if (![
      'connected', 'contact_verification_available', 'contact_verification_queued', 'contact_verified',
      'missing_contact', 'pending', 'pending_contact_verification', 'unavailable',
    ].includes(String(state))) {
      throw new Error('MY_DOHM_RESPONSE_INVALID');
    }
    return { state: state as FederationState, nativeEntryReady: value.nativeEntryReady === true };
  },
  async configureContact(email: string): Promise<MyDohmFederationStatus> {
    const value = await request('/contact/configure', { body: { email }, method: 'POST' });
    if (!['contact_verification_queued', 'pending_contact_verification'].includes(String(value.state))) {
      throw new Error('MY_DOHM_RESPONSE_INVALID');
    }
    return { state: value.state as FederationState };
  },
  async requestContact(): Promise<MyDohmFederationStatus> {
    const value = await request('/contact/request', { body: {}, method: 'POST' });
    if (!['contact_verification_queued', 'pending_contact_verification'].includes(String(value.state))) {
      throw new Error('MY_DOHM_RESPONSE_INVALID');
    }
    return { state: value.state as FederationState };
  },
  async verifyContact(token: string): Promise<'automatic' | 'queued'> {
    const value = await request('/contact/consume', { body: { token }, method: 'POST' });
    if (
      value.state !== 'contact_verified' || !['automatic', 'queued'].includes(String(value.myDohmRecognition)) ||
      value.nativeAccountPreserved !== true
    ) {
      throw new Error('MY_DOHM_RESPONSE_INVALID');
    }
    return value.myDohmRecognition as 'automatic' | 'queued';
  },
};
